import { Panel, trendClass, trendGlyph } from "../Panel"
import type { TheaterCard } from "../dashboard-types"
import { useRegion } from "../store"

/* Map a theater id back to a canonical region code the deep-dive endpoint
   understands. MidEast / Asia-EU theaters both touch MENA, but the primary
   operational geography for each theater is what we open. */
const THEATER_TO_REGION: Record<string, string> = {
  "asia-eu": "ASIA",
  "transpac": "PAC",
  "atlantic": "ATL",
  "middle-east": "MENA",
}

export function FleetPosture({ theaters, demo }: { theaters: TheaterCard[]; demo?: boolean }) {
  const openRegion = useRegion((s) => s.openRegion)
  return (
    <Panel title="Fleet Posture by Theater" demo={demo} helpText="Per-theater fleet counts and status. Logistics analog of a military posture view.">
      <div className="flex flex-col gap-2 p-2">
        {theaters.map((t) => {
          const status =
            t.status === "critical" ? { tag: "CRIT", klass: "text-red-400 border-red-500/40 bg-red-500/10" } :
            t.status === "elevated" ? { tag: "ELEV", klass: "text-orange-400 border-orange-500/40 bg-orange-500/10" } :
            { tag: "NOM", klass: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" }
          const code = THEATER_TO_REGION[t.id] ?? "GLOBAL"
          return (
            <div
              key={t.id}
              role="button"
              tabIndex={0}
              onClick={() => openRegion(code)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  openRegion(code)
                }
              }}
              className={`rounded border px-3 py-2 ${status.klass} cursor-pointer hover:brightness-125 transition focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-[color:var(--text)]">{t.label}</div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${status.klass} border`}>{status.tag}</span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                <Metric label="AIR" value={t.air} icon="✈" />
                <Metric label="SEA" value={t.sea} icon="⛴" />
                <Metric label="ROAD" value={t.road} icon="🚚" />
              </div>
              <div className="mt-1 text-[10px] flex items-center gap-1">
                <span className={trendClass(t.trend)}>{trendGlyph(t.trend)}</span>
                <span className="text-[color:var(--text-dim)] uppercase tracking-wider">{t.trend}</span>
              </div>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}

function Metric({ label, value, icon }: { label: string; value: number; icon: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">{label}</div>
      <div className="text-base tabular-nums text-[color:var(--text)]">
        <span className="opacity-60 mr-1">{icon}</span>{value}
      </div>
    </div>
  )
}
